import { createContext, useContext, useEffect, useState } from "react";
import { AppState } from "../types/AppState";
import { configurationService } from "../../../../appConfigService";
import axios from "axios";
import { ContextProviderProps } from "../../../types/ContextProviderProps";
import { BaseAssetDto } from "../../../types/BaseAssetDto";
import type { MarketDataDto } from "../../../types/MarketDataDto";
import { LocalStorageConstants } from "../../../constants/LocalStorageConstants";
import { dateTimeConversionService } from "../../../services/DateTimeConversionService";
import { portfolioMappers } from "../../../mappers/PortfolioMappers";
import { useAuthContext } from "../../../../features/authentication/hooks/use-auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { DataProviders } from "../../../types/DataProviders";
import { ViewSectionType } from "../../../../features/module-selector/types/ViewSectionType";
import { ModuleType } from "../../../../features/module-selector/types/ModuleType";
import { UserDto } from "../../../../features/options-table/types/UserDto";

const AppContext = createContext<AppState | undefined>(undefined);

export function AppContextProvider({ children }: ContextProviderProps) {
    const { userAuthData, isUserAuthenticated } = useAuthContext();
    
    const [selectedBaseAsset, setSelectedBaseAsset] = useState<BaseAssetDto | null>(null);
    const [selectedExpiration, setSelectedExpiration] = useState<string | null>(null);
    const [marketDataDto, setMarketDataDto] = useState<MarketDataDto | undefined>();
    const [selectedDataProvider, setSelectedDataProvider] = useState<DataProviders | null>(null);
    const [selectedPortfolioBalance, setSelectedPortfolioBalance] = useState<number>(0);
    const [selectedPortfolioVolume, setSelectedPortfolioVolume] = useState<number>(0);
    const [user, setUser] = useState<UserDto | null>(null);
    
    // View section selection
    // todo: ViewSection.Left from user settings?
    const [activeViewSection, setActiveViewSection] = useState<ViewSectionType>(ViewSectionType.Left);    
    const [leftViewSectionModuleType, setLeftViewSectionModuleType] = useState<ModuleType>(ModuleType.PositionsAndOrders);    
    const [rightViewSectionModuleType, setRightViewSectionModuleType] = useState<ModuleType>(ModuleType.BalanceProfile);    
    
    const navigate = useNavigate();
    
    // SignalR connection handling
    // const connection = new signalR
    //     .HubConnectionBuilder()
    //     .withUrl(`${configurationService.configuration.baseAddress}/spotsHub`)
    //     .build();
    //
    // connection.start()
    //     .then(() => console.log("Connection is started for 'spotsHub'"))
    //     .catch(err => console.log('Error while starting connection: ' + err));
    //
    // connection.on("Update", (value) => {
    //     if (Array.isArray(value)) {
    //         const updates = value as UpdateSpotDto[];
    //         updates.forEach(item => {
    //             console.log(`Symbol: ${item.symbol}, MarkPrice: ${item.markPrice}`);
    //
    //             if (marketDataDto &&
    //                 marketDataDto.BaseAsset.BaseAsset === item.symbol.slice(0, 3)
    //             ) {
    //                 marketDataDto.BaseAsset.Price = item.markPrice;
    //                 setMarketDataDto(marketDataDto);
    //             }
    //         });
    //     } else {
    //         console.warn("Unexpected update format", value);
    //     }
    // });
    
    const getCurrentUser = async (): Promise<UserDto | null> => {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }
        
        const response = await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .get<UserDto>(
                'api/users/current',
                {
                    headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`},
                });
        
        return response.data;
    }

    useEffect(() => {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }
        
        getCurrentUser().then((user: UserDto | null) => setUser(user));

        if (user !== null)
            setSelectedDataProvider(user.DataProvider);
    }, []);
    
    const fetchMarketData = async (
        baseAssetSymbol: string | null,
        expiration: string | null,
        doAfterFetch?: (value: MarketDataDto | null) => void,
    ): Promise<MarketDataDto | null> => {
        if (userAuthData === null || !isUserAuthenticated()) {
            navigate('/login');
            return null;
        }
        
        const dataProvider = selectedDataProvider === null
            ? (await getCurrentUser())!.DataProvider
            : selectedDataProvider;
        
        if (selectedDataProvider === null)
            setSelectedDataProvider(dataProvider);
        
        const query = new URLSearchParams({
            baseAssetSymbol: baseAssetSymbol ?? '',
            expireAt: dateTimeConversionService.parseAndConvertDateToShortFormattedString(expiration) ?? '',
            dataProviders: `${dataProvider}`,
        });
        
        let marketDataDto: MarketDataDto | null = null;
        
        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .get<MarketDataDto>(
                'api/market-data',
                {
                    params: query,
                    headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`},
                })
            .then((response) => {
                // setup updated marketDataDto
                marketDataDto = response.data;
                
                marketDataDto?.OptionInfos.map(x => {
                    const { price, delta, gamma, vega, theta } =
                        portfolioMappers.getAssetParameters(x.Type, x.Strike, x.ExpireAt,
                            x.MarkImpliedVolatility, x.RiskFreeInterest, marketDataDto!.UnderlyingPrice);
                
                    x.MarkPrice = price;
                    x.Delta = delta;
                    x.Gamma = gamma ?? 0;
                    x.Vega = vega ?? 0;
                    x.Theta = theta ?? 0;
                })
                
                setMarketDataDto(marketDataDto);

                // setup base asset
                if (baseAssetSymbol == null)
                    setSelectedBaseAsset(marketDataDto.AvailableBaseAssets[0]);
                else if (selectedBaseAsset == null)
                    setSelectedBaseAsset(marketDataDto.AvailableBaseAssets.find(x => x.Symbol === baseAssetSymbol) ?? null);

                // setup expiration
                const newExpiration = marketDataDto.Expirations
                    .map(x => dateTimeConversionService.convertDateToLongFormattedString(x))
                    .find(x => x.toString() === expiration)?.toString();

                if (newExpiration == null) {
                    const value = marketDataDto.Expirations[0].toString();
                    setSelectedExpiration(value);
                    localStorage.setItem(LocalStorageConstants.SelectedExpirationName, value);
                } else {
                    setSelectedExpiration(newExpiration);
                }
                
                if (doAfterFetch) {
                    doAfterFetch(marketDataDto);
                }
            })
            .catch((error: any) => {
                console.log(error);
            })
            .finally(() => {
                if (doAfterFetch)
                    doAfterFetch(null);
            });
        
        return marketDataDto;
    }
    
    const value = {
        selectedBaseAsset,
        setSelectedBaseAsset,
        selectedExpiration,
        setSelectedExpiration,
        marketDataDto,
        setMarketDataDto,
        selectedDataProvider,
        setSelectedDataProvider,
        fetchMarketData,
        activeViewSection,
        setActiveViewSection,
        leftViewSectionModuleType,
        setLeftViewSectionModuleType,
        rightViewSectionModuleType,
        setRightViewSectionModuleType,
        selectedPortfolioBalance,
        setSelectedPortfolioBalance,
        selectedPortfolioVolume,
        setSelectedPortfolioVolume,
    };
    
    return (
        <AppContext.Provider
            value={value}
        >
            {children}
        </AppContext.Provider>        
    );
}

export const useAppContext = () => {
    const context = useContext(AppContext);
    
    if (!context) {
        throw new Error('useAppContext is undefined!');
    }
    
    return context;
}
